import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@mantine/hooks';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';

import { DisclaimerModal } from './DisclaimerModal';

import styles from './AiAssistantHeader.module.scss';

type AiAssistantHeaderProps = {
  activeChatTitle?: string;
  isRailOpen: boolean;
  onClose: () => void;
  onCreateChat: () => void;
  onShowChatList: () => void;
  onTogglePinned: () => void;
  onToggleRail: () => void;
  pinned: boolean;
  view: 'chat' | 'list';
};

export function AiAssistantHeader({
  activeChatTitle,
  isRailOpen,
  onClose,
  onCreateChat,
  onShowChatList,
  onTogglePinned,
  onToggleRail,
  pinned,
  view,
}: AiAssistantHeaderProps) {
  const { t } = useTranslation();
  const [isInfoOpen, infoModal] = useDisclosure(false);
  const assistantTitle = t('app.aiAssistant.assistantTitle');
  const title =
    view === 'chat' && activeChatTitle ? activeChatTitle : assistantTitle;

  return (
    <>
      <header className={styles['root']}>
        <div className={styles['left']}>
          {view === 'chat' ? (
            <>
              <Button
                icon={<GoogleFontIcon name="chevron_left" solid />}
                onClick={onShowChatList}
                title={t('app.aiAssistant.allChats')}
                variant="text"
                data-testid="ai-assistant-all-chats"
              />
              <Button
                icon={
                  <GoogleFontIcon
                    className={styles['rail-toggle-icon']}
                    name="view_sidebar"
                    solid={isRailOpen}
                  />
                }
                onClick={onToggleRail}
                title={
                  isRailOpen
                    ? t('app.aiAssistant.hideChatList')
                    : t('app.aiAssistant.showChatList')
                }
                variant="text"
                data-testid="ai-assistant-rail-toggle"
              />
              {!isRailOpen ? (
                <Button
                  icon={<GoogleFontIcon name="add" solid />}
                  onClick={onCreateChat}
                  title={t('app.aiAssistant.newChat')}
                  variant="text"
                  data-testid="ai-assistant-header-new-chat"
                />
              ) : null}
            </>
          ) : null}
          <span className={styles['title']}>
            <SVGIcon className={styles['title-icon']} name="aiIcon" />
            <span className={styles['title-text']} title={title}>
              {title}
            </span>
          </span>
        </div>
        <div className={styles['actions']}>
          <Button
            icon={<GoogleFontIcon name="info" solid={isInfoOpen} />}
            onClick={infoModal.open}
            title={t('app.aiAssistant.disclaimerTitle')}
            variant="text"
            data-testid="ai-assistant-disclaimer"
          />
          <Button
            icon={<GoogleFontIcon name={pinned ? 'keep_off' : 'keep'} solid />}
            onClick={onTogglePinned}
            title={
              pinned
                ? t('app.aiAssistant.overlapPageContent')
                : t('app.aiAssistant.pinToPageLayout')
            }
            variant="text"
            data-testid="ai-assistant-pin"
          />
          <Button
            icon={<SVGIcon name="close" />}
            onClick={onClose}
            title={t('app.aiAssistant.closeAssistant')}
            variant="text"
            data-testid="ai-assistant-close"
          />
        </div>
      </header>
      <DisclaimerModal onClose={infoModal.close} opened={isInfoOpen} />
    </>
  );
}
